import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'throttle-debounce';

import { calculateTextSpaces, type TextSpaces } from 'shared/utils/calculateTextSpaces';
import { type EMortgageAnnotationHeightPercent } from 'shared/enums/mortgageAnnotationHeightPercent';

import styles from './AllSpaceText.css';

type AllSpaceTextProps = {
  /** Текст, который нужно растянуть по блоку */
  text: string;
  /** Высота общего блока */
  height: number;
  /** Минимальный размер шрифта */
  minFontSize?: number;
  /** Высота текста в процентах от высоты блока */
  heightPercent: EMortgageAnnotationHeightPercent.Default | EMortgageAnnotationHeightPercent.Half;
  /**
   * Вызывается по окончании подсчёта ширины текста. Если вернёт `true`, дальнейшие действия в useEffect не выполняются —
   * можно безопасно размонтировать компонент.
   */
  onAllSpaceCalculated?: (textSpaces: TextSpaces) => boolean | void;
};

const MIN_FONT_SIZE = 8;

export const AllSpaceText: FC<AllSpaceTextProps> = ({
  text,
  height,
  heightPercent,
  minFontSize = MIN_FONT_SIZE,
  onAllSpaceCalculated,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);

  const [letterSpacing, setLetterSpacing] = useState(0);
  const [wordSpacing, setWordSpacing] = useState(0);

  const adjustText = useCallback((containerEl: HTMLDivElement, textEl: HTMLSpanElement): TextSpaces => {
    const textSpaces = calculateTextSpaces(containerEl, textEl);

    setLetterSpacing(textSpaces.letterSpacing);
    setWordSpacing(textSpaces.wordSpacing);

    return textSpaces;
  }, []);

  const adjustTextDebounced = useMemo(() => debounce(500, adjustText), [adjustText]);

  useEffect(() => {
    if (!containerRef.current || !textRef.current) {
      return;
    }

    const containerEl = containerRef.current;
    const textEl = textRef.current;

    const textSpaces = adjustText(containerEl, textEl);

    if (onAllSpaceCalculated?.(textSpaces)) {
      return;
    }

    const observer = new ResizeObserver(() => adjustTextDebounced(containerEl, textEl));

    observer.observe(containerEl);

    return (): void => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  return (
    <div className={styles['container']} ref={containerRef}>
      <span
        className={styles['text']}
        ref={textRef}
        style={{
          fontSize: Math.max(minFontSize, height * (heightPercent / 100)),
          letterSpacing,
          wordSpacing,
        }}
      >
        {text}
      </span>
    </div>
  );
};
